import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  useTheme,
} from '@mui/material';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { DrawerShell } from 'src/components/atlas/molecules/Drawer/DrawerShell';
import { DiscardChangesModal } from 'src/components/CategoryConfiguration/organisms/DiscardChangesModal';
import {
  CreateMitigationPlanInput,
  MitigationPlanFragment,
  UploadSource,
  useCreateCaseDocumentationUploadUrlMutation,
  useCreateMitigationPlanMutation,
  useCreateMitigationPlanUploadMutation,
  useDisclosureCaseByIdQuery,
  useDeleteMitigationPlanUploadMutation,
  useSearchUsersPaginatedQuery,
  useUpdateMitigationPlanMutation,
} from 'src/graphql';
import { UploadScanStatus } from 'src/types';
import { isSameOrAfter, TimeUnit } from 'src/utils/time';
import { uploadToPresignedUrl } from 'src/utils/uploadToPresignedUrl';
import {
  formatValidationError,
  getFileExtension,
  validateFileUpload,
} from 'src/utils/validateFileUpload/fileValidation';
import {
  ALLOWED_EXTENSIONS,
  ExistingUpload,
  MAX_FILE_SIZE_MB,
  MAX_TOTAL_SIZE_MB,
  SelectedUser,
  Step1,
  Step2,
  Step3,
  STEPS,
  UploadedFile,
} from './AddMitigationPlanDrawerSteps';

interface Props {
  open: boolean;
  caseId: string;
  editPlan?: MitigationPlanFragment | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddMitigationPlanDrawer = ({
  open,
  caseId,
  editPlan,
  onClose,
  onSuccess,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;
  const queryClient = useQueryClient();

  const isEditMode = Boolean(editPlan);

  // Step 1 state
  const {
    data: qaData,
    isError: caseQueryError,
    isLoading: caseQueryLoading,
  } = useDisclosureCaseByIdQuery({ caseId }, { enabled: open });

  const reporterUserId = useMemo(
    () => editPlan?.signatories?.find((s) => s.isSubject)?.userId ?? null,
    [editPlan],
  );

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState(editPlan?.title ?? '');
  const [description, setDescription] = useState(editPlan?.description ?? '');
  const [deadline, setDeadline] = useState(editPlan?.deadline ?? '');
  const [reminderFrequency, setReminderFrequency] = useState(
    editPlan?.reminderFrequency ?? 7,
  );
  const [reminderMaxCount, setReminderMaxCount] = useState<number | null>(
    editPlan?.reminderMaxCount ?? 3,
  );

  // Step 2 state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [removedUploadIds, setRemovedUploadIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingRemoveUploadId, setPendingRemoveUploadId] = useState<
    string | null
  >(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 3 state
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const baselineRef = useRef({
    title: editPlan?.title ?? '',
    description: editPlan?.description ?? '',
    deadline: editPlan?.deadline ?? '',
    reminderFrequency: editPlan?.reminderFrequency ?? 7,
    reminderMaxCount: editPlan?.reminderMaxCount ?? 3,
    signatoryIds:
      editPlan?.reviewerSignatories
        ?.filter((s) => s.userId !== editPlan.createdBy)
        .map((s) => s.userId) ?? [],
  });

  const getUploadUrl = useCreateCaseDocumentationUploadUrlMutation();
  const createUploadRecord = useCreateMitigationPlanUploadMutation();
  const deleteUploadRecord = useDeleteMitigationPlanUploadMutation();
  const createPlan = useCreateMitigationPlanMutation();
  const updatePlan = useUpdateMitigationPlanMutation();

  const {
    data: usersData,
    isFetching: usersLoading,
    isError: usersError,
  } = useSearchUsersPaginatedQuery(
    {
      orderBy: { fieldName: 'name', direction: 'ASC' },
      pagination: { limit: 20, offset: 0 },
      searchBy: userSearch
        ? [{ fieldName: 'name', fieldValue: userSearch }]
        : [],
      fuzzy: undefined,
    },
    { enabled: step === 2 },
  );

  const baseline = baselineRef.current;
  const hasUnsavedChanges =
    title !== baseline.title ||
    description !== baseline.description ||
    deadline !== baseline.deadline ||
    reminderFrequency !== baseline.reminderFrequency ||
    reminderMaxCount !== baseline.reminderMaxCount ||
    uploadedFiles.length > 0 ||
    selectedUsers.length !== baseline.signatoryIds.length ||
    selectedUsers.some((u) => !baseline.signatoryIds.includes(u.id));

  const doClose = useCallback(() => {
    setStep(0);
    setTitle('');
    setDescription('');
    setDeadline('');
    setReminderFrequency(7);
    setReminderMaxCount(3);
    setUploadedFiles([]);
    setRemovedUploadIds(new Set());
    setFileError('');
    setUserSearch('');
    setSelectedUsers([]);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      doClose();
    }
  }, [hasUnsavedChanges, doClose]);

  // When editPlan changes (drawer re-opens with a different plan), sync fields
  const prevEditPlanId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (editPlan?.id === prevEditPlanId.current) return;
    prevEditPlanId.current = editPlan?.id;
    setTitle(editPlan?.title ?? '');
    setDescription(editPlan?.description ?? '');
    setDeadline(editPlan?.deadline ?? '');
    setReminderFrequency(editPlan?.reminderFrequency ?? 7);
    setReminderMaxCount(editPlan?.reminderMaxCount ?? 3);
    setUploadedFiles([]);
    setRemovedUploadIds(new Set());
    // In edit mode, only show additional signatories (exclude creator)
    const additionalSignatories =
      editPlan?.reviewerSignatories?.filter(
        (s) => s.userId !== editPlan.createdBy,
      ) ?? [];

    setSelectedUsers(
      additionalSignatories.map((s) => ({
        id: s.userId,
        name: s.user?.displayName || s.user?.name || `User ${s.userId}`,
        role: null,
        department: null,
      })),
    );
    // Reset baseline when editPlan changes
    baselineRef.current = {
      title: editPlan?.title ?? '',
      description: editPlan?.description ?? '',
      deadline: editPlan?.deadline ?? '',
      reminderFrequency: editPlan?.reminderFrequency ?? 7,
      reminderMaxCount: editPlan?.reminderMaxCount ?? 3,
      signatoryIds: additionalSignatories.map((s) => s.userId),
    };
    setStep(0);
  }, [editPlan]);

  const step1Valid =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    deadline.length > 0 &&
    isSameOrAfter(deadline, new Date(), TimeUnit.day) &&
    reminderFrequency > 0;
  const step2Valid = !uploading;
  const step3Valid = isEditMode ? !updatePlan.isLoading : !createPlan.isLoading;

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (!files.length) return;

      setFileError('');

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      files.forEach((file) => {
        const extension = getFileExtension(file.name);

        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          invalidFiles.push(file.name);
          return;
        }

        try {
          validateFileUpload({ file });
          validFiles.push(file);
        } catch (error) {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        setFileError(formatValidationError(invalidFiles));
        return;
      }
      const oversized = validFiles.find(
        (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024,
      );
      if (oversized) {
        setFileError(
          `File "${oversized.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
        );
        return;
      }

      const currentTotal = uploadedFiles.reduce(
        (sum, { file }) => sum + file.size,
        0,
      );
      const newTotal =
        validFiles.reduce((sum, f) => sum + f.size, 0) + currentTotal;
      if (newTotal > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setFileError(`Total upload size cannot exceed ${MAX_TOTAL_SIZE_MB}MB.`);
        return;
      }

      setUploading(true);
      try {
        const results: UploadedFile[] = [];
        for (const file of validFiles) {
          const { createCaseDocumentUploadURL } =
            await getUploadUrl.mutateAsync({
              type: file.type,
              filename: file.name,
              uploadSource: UploadSource.MitigationPlan,
              caseId,
              size: file.size,
            });

          const { url, s3path } = createCaseDocumentUploadURL;
          const resp = await uploadToPresignedUrl({
            file,
            url,
            tags: { uploadSource: UploadSource.MitigationPlan },
            onUploadProgress: () => {},
            controller: new AbortController(),
          });

          if (resp.status > 204) {
            if (results.length > 0) {
              setUploadedFiles((prev) => [...prev, ...results]);
            }
            setFileError(`Failed to upload "${file.name}".`);
            return;
          }

          const { createMitigationPlanUpload } =
            await createUploadRecord.mutateAsync({
              filename: file.name,
              s3path,
              size: file.size,
              mimeType: file.type,
            });

          results.push({
            file,
            uploadId: createMitigationPlanUpload.id,
            scanStatus:
              createMitigationPlanUpload.scanStatus as UploadScanStatus,
          });
        }
        setUploadedFiles((prev) => [...prev, ...results]);
      } catch {
        setFileError(
          'An unexpected error occurred during upload. Please try again.',
        );
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [caseId, uploadedFiles, getUploadUrl, createUploadRecord],
  );

  const handleRemoveFile = useCallback((uploadId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.uploadId !== uploadId));
  }, []);

  const handleRemoveExistingUpload = useCallback((uploadId: string) => {
    setPendingRemoveUploadId(uploadId);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!editPlan || !pendingRemoveUploadId) return;
    const uploadId = pendingRemoveUploadId;
    setPendingRemoveUploadId(null);

    setRemovedUploadIds((prev) => new Set([...prev, uploadId]));

    try {
      await deleteUploadRecord.mutateAsync({
        planId: editPlan.id,
        uploadId,
      });
      await queryClient.refetchQueries([
        'MitigationPlansForDisclosure',
        { caseId },
      ]);
    } catch (error: unknown) {
      setRemovedUploadIds((prev) => {
        const next = new Set(prev);
        next.delete(uploadId);
        return next;
      });
      setFileError('Failed to remove attachment. Please try again.');
    }
  }, [
    editPlan,
    pendingRemoveUploadId,
    deleteUploadRecord,
    queryClient,
    caseId,
  ]);

  const toggleUser = useCallback((user: SelectedUser) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.id === user.id);
      return exists ? prev.filter((u) => u.id !== user.id) : [...prev, user];
    });
  }, []);

  const filteredUsers = useMemo(() => {
    const allUsers = usersData?.searchUsersPaginated.items ?? [];
    const respondent = qaData?.disclosureCaseById?.respondent;

    // In create mode, wait for respondent data to prevent briefly exposing reporter
    if (!isEditMode && !respondent) return [];

    const reporterEmail = respondent?.email;
    const respondentUuid = respondent?.userId;

    return allUsers.filter((user) => {
      // 1. Filter by numeric userId (edit mode - most reliable)
      if (reporterUserId !== null && user.id === reporterUserId) return false;

      // 2. Filter by UUID match (create mode - stronger than email)
      if (respondentUuid && user.uuid && user.uuid === respondentUuid)
        return false;

      // 3. Fallback to email match (create mode - if UUID not available)
      if (
        reporterEmail &&
        user.email?.toLowerCase() === reporterEmail.toLowerCase()
      )
        return false;

      return true;
    });
  }, [usersData, reporterUserId, qaData, isEditMode]);

  const handleNext = useCallback(async () => {
    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }

    if (isEditMode) {
      const existingReviewers = editPlan!.reviewerSignatories ?? [];
      const existingUserIds = new Set(existingReviewers.map((s) => s.userId));
      const selectedUserIds = new Set(selectedUsers.map((u) => u.id));

      const addSignatoryUserIds = selectedUsers
        .map((u) => u.id)
        .filter((id) => !existingUserIds.has(id));

      const removeSignatoryIds = existingReviewers
        .filter(
          (s) =>
            s.userId !== editPlan!.createdBy && !selectedUserIds.has(s.userId),
        )
        .map((s) => s.id);

      try {
        await updatePlan.mutateAsync({
          data: {
            planId: editPlan!.id,
            title: title.trim(),
            description: description.trim() || undefined,
            deadline,
            reminderFrequency,
            reminderMaxCount: reminderMaxCount ?? undefined,
            addUploadIds:
              uploadedFiles.length > 0
                ? uploadedFiles.map((f) => f.uploadId)
                : undefined,
            addSignatoryUserIds:
              addSignatoryUserIds.length > 0 ? addSignatoryUserIds : undefined,
            removeSignatoryIds:
              removeSignatoryIds.length > 0 ? removeSignatoryIds : undefined,
          },
        });

        doClose();
        onSuccess();
      } catch {
        // mutation error surfaced via updatePlan.isError
      }
      return;
    }

    const input: CreateMitigationPlanInput = {
      caseId,
      title: title.trim(),
      description: description.trim() || undefined,
      deadline,
      reminderFrequency,
      reminderMaxCount: reminderMaxCount ?? undefined,
      uploadIds: uploadedFiles.map((f) => f.uploadId),
      signatoryUserIds: selectedUsers.map((u) => u.id),
    };

    try {
      await createPlan.mutateAsync({ data: input });
      doClose();
      onSuccess();
    } catch {
      // mutation error is surfaced via createPlan.isError
    }
  }, [
    isEditMode,
    editPlan,
    step,
    caseId,
    title,
    description,
    deadline,
    reminderFrequency,
    reminderMaxCount,
    uploadedFiles,
    selectedUsers,
    updatePlan,
    createPlan,
    doClose,
    onSuccess,
  ]);

  const nextTitle = step === 2 ? (isEditMode ? 'Save' : 'Submit') : 'Next';
  const nextDisabled =
    step === 0 ? !step1Valid : step === 1 ? !step2Valid : !step3Valid;

  return (
    <>
      <DrawerShell
        open={open}
        title={isEditMode ? 'Edit mitigation plan' : 'Add mitigation plan'}
        onRequestClose={handleClose}
        onNext={handleNext}
        onBack={step > 0 ? () => setStep((s) => s - 1) : undefined}
        nextTitle={nextTitle}
        backTitle="Previous"
        nextDisabled={nextDisabled}
        testId="add-mitigation-plan-drawer"
        disableContentScroll={step === 2}
      >
        <Stepper activeStep={step} sx={{ mb: sp[2].value }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {(createPlan.isError || updatePlan.isError) && (
          <Alert severity="error" sx={{ mb: sp[2].value }}>
            {isEditMode
              ? (updatePlan.error as Error | undefined)?.message ||
                'Failed to save the plan. Please try again.'
              : (createPlan.error as Error | undefined)?.message ||
                'Failed to submit the plan. Please try again.'}
          </Alert>
        )}

        {step === 0 && (
          <Step1
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            deadline={deadline}
            setDeadline={setDeadline}
            reminderFrequency={reminderFrequency}
            setReminderFrequency={setReminderFrequency}
            reminderMaxCount={reminderMaxCount}
            setReminderMaxCount={setReminderMaxCount}
            sp={sp}
          />
        )}

        {step === 1 && (
          <Step2
            uploadedFiles={uploadedFiles}
            existingUploads={
              isEditMode
                ? ((editPlan?.uploads as ExistingUpload[]) ?? []).filter(
                    (u) => !removedUploadIds.has(u.uploadId),
                  )
                : undefined
            }
            fileError={fileError}
            uploading={uploading}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onRemoveFile={handleRemoveFile}
            onRemoveExistingUpload={
              isEditMode ? handleRemoveExistingUpload : undefined
            }
            color={color}
            sp={sp}
          />
        )}

        {step === 2 && (
          <Step3
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            users={filteredUsers}
            usersLoading={usersLoading || caseQueryLoading}
            usersError={usersError}
            caseQueryError={caseQueryError}
            selectedUsers={selectedUsers}
            toggleUser={toggleUser}
            color={color}
            sp={sp}
          />
        )}
      </DrawerShell>

      <DiscardChangesModal
        open={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={() => {
          setIsDiscardModalOpen(false);
          doClose();
        }}
      />

      <Dialog
        open={pendingRemoveUploadId !== null}
        onClose={() => setPendingRemoveUploadId(null)}
        fullWidth
        maxWidth="sm"
        data-testid="remove-attachment-confirm-dialog"
      >
        <DialogTitle>Remove attachment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This attachment will be permanently removed from the mitigation
            plan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingRemoveUploadId(null)}
            data-testid="remove-attachment-cancel-button"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRemove}
            data-testid="remove-attachment-confirm-button"
            sx={{ textTransform: 'none', color: color.type.inverse.value }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
